import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BookOpenIcon, ChevronLeft, ChevronRight, FileText, Wand2 } from "lucide-react";
import clsx from "clsx";

import { RightDrawerDocumentName } from "./components/documentName";
import { PromptGenerator } from "@/components/feature/promptGenerator";
import { useCurrentPdf } from "@/views/chat/hooks/useCurrentPdf";
import { PDFViewer } from "@/components/feature/pdf/pdfViewer";
import { PromptLibraryRightDrawer } from "@/modules/promptLibrary/components/promptLibraryRightDrawer";

export type RightDrawerContentType = "pdf" | "promptGenerator" | "settings" | "promptLibrary" | null;

type RightDrawerProps = {
  isVisible: boolean;
  showPdfFileName?: boolean;
  toggleVisibility: () => void;
  content: RightDrawerContentType;
  setContent: (content: RightDrawerContentType) => void;
};

export const RightDrawer: React.FC<RightDrawerProps> = ({
  isVisible,
  toggleVisibility,
  content,
  setContent,
  showPdfFileName,
}) => {
  const contentRef = React.useRef<HTMLDivElement>(null);
  const { currentPdfId, currentCitation } = useCurrentPdf();

  return (
    <motion.div
      className={clsx(
        "relative flex-shrink-0 bg-ui-sidebar-background",
        isVisible ? "flex-[42%] overflow-y-auto" : "w-[45px] overflow-visible",
      )}
      style={{ minWidth: isVisible ? "300px" : "45px" }}
      initial={{ width: isVisible ? "45px" : "45px" }}
      animate={{ width: isVisible ? "42%" : "45px" }}
      transition={{ duration: 0.3 }}
    >
      <div className="absolute inset-0 flex flex-col">
        <div className="flex items-center justify-between border-b border-gray-200 bg-ui-sidebar-background p-2">
          <button onClick={toggleVisibility} className="rounded-full p-2 hover:bg-gray-200">
            {isVisible ? <ChevronRight size={24} /> : <ChevronLeft size={24} />}
          </button>
          {isVisible && (
            <div className="flex flex-wrap items-center justify-start space-x-2 lg:flex-nowrap">
              <button
                onClick={() => setContent("pdf")}
                className={clsx(
                  "flex items-center rounded p-2",
                  content === "pdf" ? "bg-blue-100" : "hover:bg-gray-200",
                )}
              >
                <FileText size={20} className="mr-2" />
                <span>PDF</span>
              </button>
              <button
                onClick={() => setContent("promptGenerator")}
                className={clsx(
                  "flex items-center rounded p-2",
                  content === "promptGenerator" ? "bg-blue-100" : "hover:bg-gray-200",
                )}
              >
                <Wand2 size={20} className="mr-2" />
                <span>Prompt&nbsp;Gen</span>
              </button>
              <button
                onClick={() => setContent("promptLibrary")}
                className={clsx(
                  "flex items-center rounded p-2",
                  content === "promptLibrary" ? "bg-blue-100" : "hover:bg-gray-200",
                )}
              >
                <BookOpenIcon size={20} className="mr-2" />
                <span>Prompt&nbsp;library</span>
              </button>
            </div>
          )}
        </div>
        <div className="flex-grow overflow-auto" ref={contentRef}>
          <AnimatePresence>
            {isVisible && (
              <motion.div
                className="h-full w-full"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {content === "pdf" && currentPdfId && (
                  <>
                    {showPdfFileName && <RightDrawerDocumentName />}
                    <PDFViewer citationHighlight={currentCitation} />
                  </>
                )}
                <PromptGenerator isVisible={content === "promptGenerator"} contentRef={contentRef} />
                {content === "promptLibrary" && <PromptLibraryRightDrawer source="qa" />}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};
